import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";
import BookingDialog from "@/components/BookingDialog";
import NumberStepper from "@/components/NumberStepper";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertTriangle, Info, Loader2, Sparkles, CheckCircle2 } from "lucide-react";

const LAYOUTS = ["straight", "l", "u", "island", "peninsula", "other", "unknown"];
const COUNT_FIELDS = [
  ["base_cabinets", "baseCabinets"], ["wall_cabinets", "wallCabinets"], ["tall_cabinets", "tallCabinets"],
  ["corner_cabinets", "cornerCabinets"], ["worktop", "worktop"], ["sink", "sink"], ["oven", "oven"],
  ["hob", "hob"], ["dishwasher", "dishwasher"], ["fridge", "fridge"], ["hood", "hood"], ["island", "island"],
  ["cover_panels", "coverPanels"], ["plinth", "plinth"], ["lighting", "lightingK"], ["other_unknown", "otherUnknown"],
];
const QUESTIONS = [
  ["old_kitchen_removed", "qOldKitchen", ["optYes", "optNo", "optNoOld"]],
  ["worktop_installation", "qWorktopInstall", ["optYes", "optNo", "optNotSure"]],
  ["sink_cutout", "qSinkCutout", ["optYes", "optNo", "optNotSure"]],
  ["hob_cutout", "qHobCutout", ["optYes", "optNo", "optNotSure"]],
  ["walls_ready", "qWallsReady", ["optYes", "optNo", "optNotSure"]],
];

const DEFAULT_CONFIG = {
  layout: "unknown", base_cabinets: 0, wall_cabinets: 0, tall_cabinets: 0, corner_cabinets: 0,
  worktop: 0, worktop_length_cm: "", sink: 0, oven: 0, hob: 0, dishwasher: 0, fridge: 0, hood: 0,
  cover_panels: 0, plinth: 0, island: 0, lighting: 0, other_unknown: 0,
};

export default function KitchenPage() {
  const { t, lang } = useLang();
  const sk = t.seo.kitchen;
  const [fileIds, setFileIds] = useState([]);
  const [recognizing, setRecognizing] = useState(false);
  const [started, setStarted] = useState(false); // AI done OR manual chosen
  const [recognition, setRecognition] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [answers, setAnswers] = useState({ old_kitchen_removed: "", worktop_installation: "", sink_cutout: "", hob_cutout: "", walls_ready: "", appliances_to_install: "" });
  const [open, setOpen] = useState(false);
  const [undetermined, setUndetermined] = useState({});

  useEffect(() => {
    document.title = lang === "da"
      ? "IKEA køkken montering & installation — send projekt | Belov Multiservice"
      : "IKEA kitchen assembly & installation — send project | Belov Multiservice";
  }, [lang]);

  const upd = (k, v) => {
    setConfig((c) => ({ ...c, [k]: v }));
    setUndetermined((u) => { if (!u[k]) return u; const n = { ...u }; delete n[k]; return n; });
  };
  const updA = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const analyze = async () => {
    if (!fileIds.length) { toast.error(t.kitchen.analyzeFailed); return; }
    setRecognizing(true);
    try {
      const res = await api.post("/kitchen/recognize", { file_id: fileIds[0] });
      const r = res.data.recognition;
      setRecognition(r);
      const next = { ...DEFAULT_CONFIG };
      const und = {};
      COUNT_FIELDS.forEach(([k]) => {
        if (typeof r[k] === "number") next[k] = r[k];
        else if (r[k] === null || r[k] === undefined) und[k] = true; // AI could not determine
      });
      next.layout = r.layout || "unknown";
      next.worktop_length_cm = r.worktop_length_cm || "";
      setConfig(next);
      setUndetermined(und);
      setStarted(true);
    } catch (e) {
      toast.error(t.kitchen.analyzeFailed);
      setStarted(true);
    } finally {
      setRecognizing(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none text-base";
  const items = recognition?.items || [];

  return (
    <div data-testid="kitchen-page">
      <section className="bg-[#F4F1EA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 px-3 py-1.5 rounded-full">{t.services.quote}</span>
          <h1 className="mt-4 text-3xl sm:text-5xl font-display font-extrabold tracking-tight text-stone-900">{t.kitchen.heroTitle}</h1>
          <p className="mt-4 text-lg text-stone-600">{t.kitchen.heroSubtitle}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="kitchen-noprice-notice">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900 leading-relaxed">{t.kitchen.noPriceNote}</p>
        </div>

        {/* STEP 1 — Upload */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold text-stone-900">{t.kitchen.uploadStep}</h2>
          <p className="text-sm text-stone-500 mt-1 mb-4">{t.kitchen.uploadHint}</p>
          <FileUpload onChange={setFileIds} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" testId="kitchen-upload" />
          {!started && (
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button onClick={analyze} disabled={recognizing || !fileIds.length}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#1E3A2F] text-white font-bold hover:bg-[#162D24] disabled:opacity-50"
                data-testid="kitchen-analyze-btn">
                {recognizing ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.kitchen.analyzing}</> : <><Sparkles className="w-4 h-4" /> {t.kitchen.analyze}</>}
              </button>
              <button onClick={() => setStarted(true)} disabled={recognizing}
                className="px-5 py-3.5 rounded-full border border-stone-300 font-semibold text-stone-700 hover:bg-stone-50"
                data-testid="kitchen-manual-btn">
                {t.kitchen.fillManually}
              </button>
            </div>
          )}
        </div>

        {started && (
          <>
            {/* STEP 2 — AI summary (editable) */}
            <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8" data-testid="kitchen-summary">
              <div className="flex items-center gap-2 text-[#1E3A2F]"><CheckCircle2 className="w-5 h-5" /><h2 className="font-display text-xl font-bold">{t.kitchen.summaryStep}</h2></div>
              <p className="text-sm text-stone-500 mt-1">{t.kitchen.summaryHint}</p>

              {recognition?.low_confidence && (
                <p className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="kitchen-low-confidence">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {t.kitchen.lowConfidence}
                </p>
              )}

              {items.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-widest text-stone-400 font-semibold mb-2">{t.kitchen.recognizedItems}</div>
                  <ul className="space-y-1 text-sm max-h-40 overflow-y-auto pr-1">
                    {items.map((it, i) => (
                      <li key={i} className={`flex justify-between gap-2 ${it.confidence < 0.6 ? "text-amber-700" : "text-stone-600"}`} data-testid={`kitchen-item-${i}`}>
                        <span className="truncate">{it.quantity}× {it.name || it.category}{it.article ? ` (${it.article})` : ""}</span>
                        <span className="font-mono text-xs shrink-0">{Math.round((it.confidence || 0) * 100)}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Layout */}
              <div className="mt-5">
                <div className="text-sm font-medium text-stone-800 mb-2">{t.kitchen.layoutLabel}</div>
                <div className="flex flex-wrap gap-2">
                  {LAYOUTS.map((l) => (
                    <button key={l} type="button" onClick={() => upd("layout", l)}
                      className={`px-3.5 py-2 rounded-xl border text-sm font-medium ${config.layout === l ? "border-[#1E3A2F] bg-[#1E3A2F] text-white" : "border-stone-300 text-stone-700 hover:bg-stone-50"}`}
                      data-testid={`kitchen-layout-${l}`}>{t.kitchen.layouts[l]}</button>
                  ))}
                </div>
              </div>

              {/* Counts */}
              <div className="mt-4 divide-y divide-stone-100">
                {COUNT_FIELDS.map(([k, tk]) => (
                  <NumberStepper key={k} testId={`kitchen-${k}`} label={t.kitchen[tk]} value={config[k]} onChange={(v) => upd(k, v)} max={100} hint={undetermined[k] ? t.kitchen.optNotSure : undefined} />
                ))}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-stone-700 mb-1.5">{t.kitchen.worktopLength}</label>
                <input inputMode="numeric" className={inputCls} value={config.worktop_length_cm}
                  onChange={(e) => upd("worktop_length_cm", e.target.value.replace(/\D/g, ""))} data-testid="kitchen-worktop-length" />
              </div>
            </div>

            {/* STEP 3 — Questions */}
            <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8" data-testid="kitchen-questions">
              <h2 className="font-display text-xl font-bold text-stone-900">{t.kitchen.questionsStep}</h2>
              <div className="mt-4 space-y-4">
                {QUESTIONS.map(([key, qk, opts]) => (
                  <div key={key} data-testid={`kitchen-q-${key}`}>
                    <div className="text-sm font-medium text-stone-800 mb-1.5">{t.kitchen[qk]}</div>
                    <div className="flex flex-wrap gap-2">
                      {opts.map((o) => (
                        <button key={o} type="button" onClick={() => updA(key, o)}
                          className={`px-3.5 py-2 rounded-xl border text-sm font-medium ${answers[key] === o ? "border-[#1E3A2F] bg-[#1E3A2F] text-white" : "border-stone-300 text-stone-700 hover:bg-stone-50"}`}
                          data-testid={`kitchen-${key}-${o}`}>{t.kitchen[o]}</button>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-stone-800 mb-1.5">{t.kitchen.qAppliances}</label>
                  <input className={inputCls} placeholder={t.kitchen.qAppliancesPh} value={answers.appliances_to_install}
                    onChange={(e) => updA("appliances_to_install", e.target.value)} data-testid="kitchen-appliances" />
                </div>
              </div>
              <div className="mt-5 flex items-start gap-2 text-sm text-stone-600 bg-stone-50 rounded-xl p-4">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-stone-400" /> {t.kitchen.regulatedNote}
              </div>
            </div>

            <button onClick={() => setOpen(true)} className="w-full py-4 rounded-full bg-[#1E3A2F] text-white font-bold text-base hover:bg-[#162D24]" data-testid="kitchen-send-btn">
              {t.kitchen.sendProject}
            </button>
          </>
        )}
      </section>

      {/* SEO CONTENT */}
      <section className="bg-[#F4F1EA]" data-testid="kitchen-seo">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 space-y-12">
          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{sk.aboutTitle}</h2>
            <p className="mt-4 text-stone-600 leading-relaxed">{sk.aboutText}</p>
            <p className="mt-4 text-stone-600">{sk.worksIntro}</p>
            <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
              {sk.worksItems.map((it, i) => (
                <li key={i} className="flex items-center gap-2.5 text-stone-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> <span>{it}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-stone-500 leading-relaxed">{sk.worksNote}</p>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{sk.howTitle}</h2>
            <ol className="mt-6 grid sm:grid-cols-3 gap-5">
              {sk.howSteps.map((st, i) => (
                <li key={i} className="bg-white border border-stone-200 rounded-2xl p-6">
                  <span className="font-mono text-2xl font-bold text-stone-200">0{i + 1}</span>
                  <p className="mt-2 text-sm text-stone-700 leading-relaxed">{st}</p>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{sk.faqTitle}</h2>
            <Accordion type="single" collapsible className="mt-6" data-testid="kitchen-faq">
              {sk.faqs.map((f, i) => (
                <AccordionItem key={i} value={`kitchen-faq-${i}`} className="border-stone-200">
                  <AccordionTrigger className="text-left font-semibold text-stone-900 hover:no-underline" data-testid={`kitchen-faq-q-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <BookingDialog
        open={open}
        onOpenChange={setOpen}
        serviceType="kitchen"
        configuration={{ ...config, answers }}
        fileIds={fileIds}
        recognizedComponents={items}
        source={recognition ? "upload" : "manual"}
        successText={t.kitchen.successMsg}
      />
    </div>
  );
}
